import React, { useState } from 'react';
import { toast } from 'react-toastify';
import CustomModal from '../../../components/customModal/CustomModal';
import { Button, Form, Table } from 'react-bootstrap';
import { useFormik } from 'formik';
import './AddAthenaOrderDialog.scss';
import useNdcWhitelistSetup from '../../../Hooks/useNdcWhitelistSetup';
import Loading from '../../../components/LoadingPage/Loading';
import { selectCurrentUserId } from '../../../redux/auth/authSlice';
import { useAppSelector } from '../../../redux/hooks';

interface OrderItem {
    orderTypeId: string;
    name: string;
}

interface AddAthenaOrderDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const typeOpts = [
    { label: "Vaccine", value: "Vaccine" },
    { label: "Prescription", value: "Prescription" },
    { label: "DME", value: "DME" },
];

const AddAthenaOrderDialog: React.FC<AddAthenaOrderDialogProps> = ({ isOpen, onClose }) => {
    const [searchResults, setSearchResults] = useState<OrderItem[]>([]);
    const [selectedOrders, setSelectedOrders] = useState<OrderItem[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [failedOrderErrors, setFailedOrderErrors] = useState<Record<string, string>>({});

    const { searchAthenaOrdersAPI, searchAthenaOrdersLoading, upsertAthenaOrderMasterAPI } = useNdcWhitelistSetup();
    const currentUserId = useAppSelector(selectCurrentUserId);

    const formik = useFormik({
        initialValues: {
            type: 'Vaccine',
            searchText: '',
        },
        onSubmit: async (values) => {
            try {
                const res = await searchAthenaOrdersAPI({ type: values.type, searchParam: values.searchText });
                setSearchResults(res || []);
            } catch (error) {
                setSearchResults([]);
                console.error("Error while searching for order: ", error);
            } finally {
                setHasSearched(true);
            }
        },
    });

    const handleAddAthenaOrder = async (orders: OrderItem[]) => {
        if (orders.length === 0) return;

        setIsProcessing(true);
        setFailedOrderErrors({});
        const failedOrders: { order: OrderItem; error: string }[] = [];
        let successCount = 0;

        for (const order of orders) {
            try {
                const res = await upsertAthenaOrderMasterAPI({
                    ...order,
                    isActive: true,
                    createdBy: currentUserId,
                });
                if (res?.success) {
                    successCount++;
                } else {
                    failedOrders.push({
                        order,
                        error: res?.message || 'Unknown error',
                    });
                }
            } catch (error: any) {
                failedOrders.push({
                    order,
                    error: error?.message || 'Unknown error',
                });
            }
        }

        setIsProcessing(false);

        if (successCount > 0) {
            toast.success(`${successCount} order(s) added successfully.`);
        }

        if (failedOrders.length > 0) {
            // Build error map for table row indicators
            const errorMap: Record<string, string> = {};
            failedOrders.forEach(f => {
                errorMap[f.order.orderTypeId] = f.error;
            });
            setFailedOrderErrors(errorMap);

            toast.error(
                `${failedOrders.length} order(s) failed to add.`,
                { autoClose: false }
            );
        } else {
            // All succeeded — close the dialog
            handleCloseDialog();
        }
    };

    const handleCheckboxChange = (order: OrderItem) => {
        setSelectedOrders(prev => {
            const isAlreadySelected = prev.some(item => item.orderTypeId === order.orderTypeId);
            if (isAlreadySelected) {
                return prev.filter(item => item.orderTypeId !== order.orderTypeId);
            } else {
                return [...prev, order];
            }
        });
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedOrders(searchResults);
        } else {
            setSelectedOrders([]);
        }
    };

    const handleAddSelectedOrders = () => {
        handleAddAthenaOrder(selectedOrders);
    };

    const handleAddAllOrders = () => {
        handleAddAthenaOrder(searchResults);
    };

    const handleClearFilters = () => {
        formik.resetForm();
        setSearchResults([]);
        setSelectedOrders([]);
        setHasSearched(false);
        setFailedOrderErrors({});
    }

    const handleCloseDialog = () => {
        handleClearFilters();
        onClose();
    };

    const isAllSelected = searchResults.length > 0 && selectedOrders.length === searchResults.length;
    const isLoading = searchAthenaOrdersLoading || isProcessing;

    if (!isOpen) return null;

    return (
        <>
            {isLoading && <Loading />}
            <CustomModal
                onClose={handleCloseDialog}
                title="Add Order"
                overlayClass='add-athena-order-modal'
                content={
                    <div className='add-athena-order-modal__content'>
                        <Form onSubmit={formik.handleSubmit} className='add-athena-order-modal__filter-form'>
                            <Form.Group className='add-athena-order-modal__form-field'>
                                <Form.Label>Type:</Form.Label>
                                <div className='add-athena-order-modal__type-radio-group'>
                                    {typeOpts.map((opt, i) => (
                                        <Form.Check
                                            key={i}
                                            inline
                                            label={opt.label}
                                            value={opt.value}
                                            id={`type-${opt.value}`}
                                            type="radio"
                                            name="type"
                                            checked={formik.values.type === opt.value}
                                            onChange={formik.handleChange}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                            <Form.Group className='add-athena-order-modal__form-field'>
                                <Form.Label>Search:</Form.Label>
                                <Form.Control
                                    name="searchText"
                                    placeholder="Search"
                                    value={formik.values.searchText}
                                    onChange={formik.handleChange}
                                />
                            </Form.Group>
                            <Form.Group className='add-athena-order-modal__form-actions'>
                                <Button variant="primary" type="submit">
                                    Search
                                </Button>
                                <Button variant="outline-secondary" type="button" onClick={handleClearFilters}>
                                    Cancel
                                </Button>
                            </Form.Group>
                        </Form>

                        {/* Results Table */}
                        <div className="add-athena-order-modal__table-wrapper">
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th className="add-athena-order-modal__checkbox-col">
                                            <Form.Check
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                disabled={searchResults.length === 0}
                                            />
                                        </th>
                                        <th>Order Type Id</th>
                                        <th>Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchResults.length > 0 ? (
                                        searchResults.map((item) => {
                                            const isSelected = selectedOrders.some((ord) => ord.orderTypeId === item.orderTypeId);
                                            const errorMsg = failedOrderErrors[item.orderTypeId];
                                            const rowClass = [isSelected ? 'selected' : '', errorMsg ? 'add-athena-order-modal__row--error' : ''].filter(Boolean).join(' ');
                                            return (
                                                <tr
                                                    key={item.orderTypeId}
                                                    className={rowClass}
                                                    onClick={() => handleCheckboxChange(item)}
                                                >
                                                    <td className="add-athena-order-modal__checkbox-col">
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleCheckboxChange(item)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </td>
                                                    <td title={item.orderTypeId}>
                                                        {item.orderTypeId}
                                                        {errorMsg && (
                                                            <i
                                                                className="bi bi-info-circle-fill add-athena-order-modal__error-icon"
                                                                title={errorMsg}
                                                            />
                                                        )}
                                                    </td>
                                                    <td title={item.name}>{item.name}</td>
                                                </tr>);
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={3} className="text-center">
                                                {hasSearched ? 'No records found' : 'Search to view orders'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                }
                footer={
                    <div className="add-athena-order-modal__footer">
                        <span className="add-athena-order-modal__record-count">
                            {searchResults.length} record(s)
                        </span>
                        <div className="add-athena-order-modal__footer-actions">
                            <Button
                                variant="outline-primary"
                                onClick={handleAddAllOrders}
                                disabled={searchResults.length === 0}
                            >
                                Add All
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleAddSelectedOrders}
                                disabled={selectedOrders.length === 0}
                            >
                                Add Selected
                            </Button>
                        </div>
                    </div>
                }
            />
        </>
    );
};

export default AddAthenaOrderDialog;